// "Who's playing?": name + birthday month sign-in, switching player, and the Friends screen.
import { identity } from './identity.js';
import { saves } from './save.js';
import { MONTHS, makeCode, mmyyFrom, nameAllowed, normaliseRoomCode, sanitiseName } from './codes.js';
import { whoScreenHTML, whoConflictHTML, friendsScreenHTML, worldsOnlineHTML } from './friends-ui.js';
import { LobbyPoller } from './lobby.js';
import { audio } from './audio.js';
import { escapeHtml } from './util.js';

const ERRORS = {
  name: 'Please choose a different name. Use letters and numbers.',
  mmyy: 'Please pick your birthday month and year.',
  offline: 'We can’t reach the internet right now. You can play on this device for now.',
  slow: 'Lots of tries! Wait a few minutes, then try again.',
  server: 'Something went wrong. Please try again in a moment.',
  pull: 'We found you, but couldn’t load your game. Check the internet and try again.',
};

export class Accounts {
  constructor(game) {
    this.game = game;
    this.skipped = false;
    this.form = { name: '', month: null, year: null };
    this.done = null;
  }

  get ui() {
    return this.game.ui;
  }

  // Keep the save pointing at the signed-in player's game. Nothing here makes the save newer, so the boot
  // sync still prefers a newer cloud copy.
  boot() {
    identity.load();
    const id = identity.data;
    if (!id) return;
    const s = saves.data;
    if (s.cloudCode === id.cloudCode && (!s.playerId || s.playerId === id.playerId)) {
      // This player's game (a sign-in can stop before the player id was saved).
      if (!s.playerId) {
        s.playerId = id.playerId;
        saves.store();
      }
      return;
    }
    // Another game (or a lost or unreadable one) is on this device. Keep it safe under its own code, then
    // stand in for this player's game until the boot sync loads it. A stand-in is never uploaded.
    if (!s.needsPull && (s.playerId || saves.hasProgress()) && !saves.backup(s)) {
      // If the device cannot keep a recovery copy, retain its current game and ask for sign-in again.
      identity.clear();
      this.ui?.toast?.('Your current game is safe. Free some space on this device before switching players.', 'blue', 5000);
      return;
    }
    saves.adopt(null, id.cloudCode, id.playerId, { push: false, hydrate: true });
    if (id.name) saves.data.name = id.name;
    saves.store();
  }

  get loggedIn() {
    return identity.loggedIn;
  }

  // Also after a long break: sign-ins last 180 days of not playing.
  needsWho() {
    return !(identity.loggedIn && identity.tokenValid()) && !this.skipped && location.protocol !== 'file:';
  }

  // Shows the sign-in screen; resolves once the child signs in or chooses to play on this device.
  who({ canSkip = true } = {}) {
    this.canSkip = canSkip;
    const s = saves.data;
    if (!this.form.name) this.form.name = identity.data?.name || (s.playerId ? '' : s.name);
    this.render();
    return new Promise((resolve) => (this.done = resolve));
  }

  render(error = '', busy = false) {
    this.ui.screen = 'who';
    this.ui.setScreen(whoScreenHTML({ ...this.form, error, busy, canSkip: this.canSkip }), 'scr-who');
    if (!busy) this.ui.screenEl.querySelector('.who-name')?.focus({ preventScroll: true });
  }

  readForm() {
    const root = this.ui.screenEl;
    const name = root.querySelector('.who-name'), month = root.querySelector('.who-month'), year = root.querySelector('.who-year');
    if (name) this.form = { name: sanitiseName(name.value), month: Number(month.value) || null, year: Number(year.value) || null };
    return this.form;
  }

  async submit({ accept = false, fresh = false } = {}) {
    if (this.busy) return;
    const f = this.ui.screen === 'who' ? this.readForm() : this.form;
    if (!nameAllowed(f.name)) return this.render(ERRORS.name);
    const mmyy = mmyyFrom(f.month, f.year);
    if (!mmyy) return this.render(ERRORS.mmyy);
    const s = saves.data;
    // Progress made before sign-in existed joins this player's card instead of being lost.
    const claimCode = !fresh && !s.playerId && saves.hasProgress() ? s.cloudCode : undefined;
    this.busy = true;
    this.ui.closeModal();
    this.render('', true);
    const r = await identity.login({ name: f.name, mmyy, claimCode, accept });
    this.busy = false;
    if (!r.ok) {
      if (r.error === 'conflict') {
        this.render();
        this.ui.modal(whoConflictHTML({ reason: r.reason, name: r.name || f.name, monthName: MONTHS[f.month - 1], year: f.year, oldCode: s.cloudCode }), 'modal-who');
        return;
      }
      return this.render(ERRORS[r.error] || ERRORS.server);
    }
    if (!(await this.adopt(r))) {
      identity.clear();
      return this.render(ERRORS.pull);
    }
    audio.play('unlock');
    this.ui.toast(`Hi ${escapeHtml(r.data.name)}! Your world code is <b>${r.data.roomCode}</b> 🌍`, 'green', 5000);
    this.finish();
  }

  // Point this device at the signed-in player's game.
  async adopt(r) {
    const d = r.data, s = saves.data;
    if (s.cloudCode === d.cloudCode) {
      s.playerId = d.playerId;
      if (r.created) {
        // This device's game has just become the new player's, so it is the newest copy.
        s.name = d.name;
        saves.persist(true);
        return true;
      }
      // Already this player's game: keep whichever copy is newer, without making this one newer.
      saves.store();
      if ((await saves.syncOnBoot()) === 'pulled') this.game.afterSaveSwap();
      return true;
    }
    // Keep the previous game safe under its own code: a device copy stays until its upload is confirmed.
    // A device that can't keep a copy switches only once the upload has worked.
    if (!s.needsPull && (s.playerId || saves.hasProgress()) && !saves.backup(s) && !(await saves.pushCloud())) return false;
    const remote = r.created ? null : await saves.pullCloud(d.cloudCode, 6000);
    if (!r.created && !remote && saves.lastPull !== 'missing') return false;
    saves.adopt(remote, d.cloudCode, d.playerId);
    if (!remote) {
      saves.data.name = d.name;
      saves.persist(true);
    }
    this.game.afterSaveSwap();
    return true;
  }

  skip() {
    this.skipped = true;
    this.finish();
  }

  back() {
    this.ui.closeModal();
    this.render();
  }

  finish() {
    this.ui.closeModal();
    const done = this.done;
    this.done = null;
    if (done) done();
    else this.showFriends();
  }

  async switchPlayer() {
    const name = saves.data.name;
    const ok = await this.ui.confirm('Switch player?', `We’ll save ${escapeHtml(name)}’s game online first. Then the next player can sign in.`, 'Switch player', 'Cancel');
    this.ui.closeModal();
    if (!ok) return;
    // A stand-in has nothing to keep: the player's real game is already safe in the cloud.
    if (!saves.data.needsPull && !(await saves.pushCloud())) return this.ui.toast('Connect to the internet first, so this game stays safe.', 'blue', 4500);
    identity.clear();
    saves.adopt(null, makeCode(), null, { push: false });
    this.game.afterSaveSwap();
    this.form = { name: '', month: null, year: null };
    this.skipped = false;
    this.game.enterMenuScene('title');
    await this.who({ canSkip: true });
    this.game.showMenu();
  }

  async startWho() {
    this.game.enterMenuScene('title');
    await this.who({ canSkip: true });
    this.showFriends();
  }

  showFriends(message = '') {
    const id = identity.data;
    this.leaveFriends();
    if (this.game.state !== 'menu') this.game.enterMenuScene('worlds');
    this.ui.screen = 'friends';
    this.ui.setScreen(
      friendsScreenHTML({
        loggedIn: identity.loggedIn,
        name: id?.name || saves.data.name,
        roomCode: id?.roomCode || '',
        recent: saves.data.friends.recent,
        status: navigator.onLine === false ? 'offline' : 'unknown',
        message,
      }),
      'scr-friends',
    );
    // "Friends playing now" asks every 10 s while this screen is showing (it stops itself once it isn't).
    this.worldsHTML = null;
    if (identity.loggedIn) {
      this.lobby = new LobbyPoller({ every: 10000, active: () => this.onFriendsScreen(), onUpdate: (r) => this.renderWorlds(r) });
      this.lobby.start();
    }
  }

  onFriendsScreen() {
    const el = this.ui?.screenEl;
    return !!el && el.classList.contains('scr-friends') && el.classList.contains('show');
  }

  // Refresh only the list, and only when it changed, so a tap on Visit is never lost to a redraw.
  renderWorlds(r) {
    const box = this.onFriendsScreen() && this.ui.screenEl.querySelector('.worlds-online');
    if (!box) return this.leaveFriends();
    const html = worldsOnlineHTML({ worlds: r?.ok ? r.worlds : null, myCode: identity.data?.roomCode || r?.you || '', error: r?.ok ? '' : r?.error || 'server' });
    if (html !== this.worldsHTML) box.innerHTML = this.worldsHTML = html;
  }

  leaveFriends() {
    this.lobby?.stop();
    this.lobby = null;
  }

  // Ask for "Friends playing now" straight away (the test harness uses this instead of waiting 10 s).
  pollWorlds() {
    return this.lobby ? this.lobby.refresh() : Promise.resolve(null);
  }

  async copyRoomCode(code = identity.data?.roomCode) {
    if (!code) return;
    try {
      await navigator.clipboard.writeText(code);
      this.ui.toast('📋 World code copied!', 'blue');
    } catch (e) {
      this.ui.toast('World code: ' + code, 'blue', 6000);
    }
  }

  async joinWorld(raw) {
    if (this.joining) return;
    const input = raw ?? this.ui.screenEl.querySelector('.friends-code')?.value;
    const code = normaliseRoomCode(input);
    if (!code) return this.showFriends('That code doesn’t look right. It looks like ROCKET-472.');
    if (!identity.loggedIn) {
      await this.startWho();
      if (!identity.loggedIn) return;
    }
    if (code === identity.data.roomCode) return this.game.startExplore();
    // From the friends panel inside Explore (Friends playing now), a problem keeps you in the world you are in.
    const panel = this.game.state === 'explore' && this.game.mp;
    const stay = (text) => {
      if (!panel) return this.showFriends(text);
      this.ui.toast(escapeHtml(text), 'blue', 4500);
      this.game.mp?.openPanel();
    };
    this.joining = true;
    const info = await fetchJSON(`/api/room/${code}`);
    this.joining = false;
    if (!info) return stay('We can’t reach the internet right now. Try again soon.');
    if (!info.exists) return stay(`We couldn’t find ${code}. Check the code with your friend.`);
    if (info.locked) return stay(`${info.ownerName}’s world is locked right now. Ask them to unlock it.`);
    const ok = await this.ui.confirm(`Visit ${escapeHtml(info.ownerName)}’s world? ✈️`, `World code ${code}. You can build together and learn together. Your bag comes with you.`, 'Let’s go!', 'Not now');
    this.ui.closeModal();
    if (ok) {
      this.leaveFriends();
      this.game.startExplore({ room: code, ownerName: info.ownerName });
    } else if (panel) this.game.mp?.openPanel();
  }

  rememberWorld(code, name) {
    const f = (saves.data.friends ||= { recent: [] });
    f.recent = [{ code, name, at: Date.now() }, ...f.recent.filter((w) => w.code !== code)].slice(0, 6);
    saves.persist();
  }
}

async function fetchJSON(url, wait = 5000) {
  const controller = new AbortController();
  const timer = setTimeout(() => controller.abort(), wait);
  try {
    const res = await fetch(url, { cache: 'no-store', signal: controller.signal });
    return res.ok ? await res.json() : null;
  } catch (e) {
    return null;
  } finally {
    clearTimeout(timer);
  }
}
